import type { EmploymentStatus } from '@/types/hrInterview'

/**
 * Los datos consolidados del candidato para la vista de Dirección (RH-03).
 *
 * Dos de ellos viven en dos lugares a la vez: la expectativa salarial y la
 * ciudad las declara el candidato en el formulario público, y RH las vuelve a
 * capturar en la entrevista. **Gana la de la entrevista**: es posterior y la
 * tomó alguien de RH con el candidato enfrente, mientras que la del formulario
 * es lo que la persona escribió antes de hablar con nadie.
 *
 * No se descarta la del formulario: si RH no capturó la suya, se muestra esa
 * en vez de un guión.
 */

export interface FuentesDeInformacionGeneral {
  candidato: {
    experienceDescription?: string | null
    experienceYears?: number | null
    residenceCity?: string | null
    salaryExpectation?: number | string | null
  }
  entrevista: {
    previousMonthlySalary?: number | string | null
    salaryExpectation?: number | string | null
    employmentStatus?: EmploymentStatus | null
    availability?: string | null
    residenceCity?: string | null
  } | null
  /** El rango que RH le asignó a la vacante, no al candidato. */
  vacante: {
    salarioMinimo?: number | string | null
    salarioMaximo?: number | string | null
  } | null
}

export interface InformacionGeneral {
  resumenProfesional: string | null
  experienceYears: number | null
  salarioAnterior: number | string | null
  expectativaSalarial: number | string | null
  situacionLaboral: EmploymentStatus | null
  disponibilidad: string | null
  residencia: string | null
  rangoDeLaVacante: { min: number | string; max: number | string } | null
}

/** Una cadena solo cuenta si trae algo además de espacios. */
const texto = (valor?: string | null): string | null => valor?.trim() || null

/** `0` es un importe válido; `null`, `undefined` y `''` no lo son. */
const importe = (
  valor?: number | string | null,
): number | string | null =>
  valor === null || valor === undefined || valor === '' ? null : valor

export const informacionGeneralDelCandidato = (
  fuentes: FuentesDeInformacionGeneral,
): InformacionGeneral => {
  const { candidato, entrevista, vacante } = fuentes

  const min = importe(vacante?.salarioMinimo)
  const max = importe(vacante?.salarioMaximo)

  return {
    resumenProfesional: texto(candidato.experienceDescription),
    experienceYears: candidato.experienceYears ?? null,
    salarioAnterior: importe(entrevista?.previousMonthlySalary),
    expectativaSalarial:
      importe(entrevista?.salaryExpectation) ??
      importe(candidato.salaryExpectation),
    situacionLaboral: entrevista?.employmentStatus ?? null,
    disponibilidad: texto(entrevista?.availability),
    residencia: texto(entrevista?.residenceCity) ?? texto(candidato.residenceCity),
    // El rango solo sirve completo: con un solo extremo no se puede comparar
    // contra lo que pide el candidato, que es para lo que está en la pantalla.
    rangoDeLaVacante: min !== null && max !== null ? { min, max } : null,
  }
}

/**
 * Si no hay ni un dato capturado, la pantalla muestra un aviso en vez de una
 * reja de guiones, que se lee como que algo falló al cargar.
 */
export const sinDatosConsolidados = (info: InformacionGeneral): boolean =>
  info.salarioAnterior === null &&
  info.expectativaSalarial === null &&
  info.situacionLaboral === null &&
  info.disponibilidad === null &&
  info.residencia === null &&
  info.rangoDeLaVacante === null
